/**
 * CV/Resume API Service
 *
 * FIX: All functions now return consistent shapes:
 * - Success: { success: true, data: <response>, status: <http status> }
 * - Error: { success: false, error: <message>, status: <http status>, requiresAuth: <bool> }
 */

import http from '../client';
import { ENDPOINTS } from '../config';

// ==================== Helper Functions ====================

/**
 * FIX: Standardized error handler that preserves HTTP status and auth state
 * FIX #6: Added 403 Forbidden handling for IDOR protection
 */
const handleApiError = (error) => {
  const status = error.status || error.response?.status || 0;
  const message = error.message || 'An error occurred';

  // Check if this is an auth error (401)
  if (status === 401) {
    return {
      success: false,
      error: 'Authentication required',
      status: 401,
      requiresAuth: true
    };
  }

  // FIX #6: Handle 403 Forbidden (IDOR protection)
  if (status === 403) {
    return {
      success: false,
      error: 'Access denied: You can only access your own CV',
      status: 403,
      requiresAuth: false,
      accessDenied: true
    };
  }

  return {
    success: false,
    error: message,
    status: status
  };
};

/**
 * FIX: Standardized success handler for consistent return shape
 * Properly unwraps backend response which comes as { success, data, message }
 */
const handleApiSuccess = (response) => {
  // Backend returns { success, data, message } wrapper
  // response.data contains this wrapper, so actual data is in response.data.data
  const backendResponse = response.data;

  // If backend uses { success, data } wrapper, extract the inner data
  // Otherwise use the response data directly
  const actualData = (backendResponse && backendResponse.data !== undefined)
    ? backendResponse.data
    : backendResponse;

  return {
    success: backendResponse?.success ?? true,
    data: actualData,
    status: response.status || 200,
    message: backendResponse?.message
  };
};

// ==================== JWT-based endpoints (recommended) ====================

/**
 * Find or create CV for current user using JWT token
 * @returns {Promise<Object>}
 */
export const findOrCreateMyCv = async () => {
  try {
    const response = await http.post(ENDPOINTS.CV.ME);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== CV by ID ====================

/**
 * Update an existing CV
 * @param {number} cvId
 * @param {Object} cvData
 * @returns {Promise<Object>}
 */
export const updateCv = async (cvId, cvData) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE(cvId), cvData);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Education ====================

/**
 * Add education to CV
 * @param {number} cvId
 * @param {Object} education
 * @returns {Promise<Object>}
 */
export const addEducation = async (cvId, education) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_EDUCATION(cvId), education);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update education entry
 * @param {number} cvId
 * @param {number} educationId
 * @param {Object} education
 * @returns {Promise<Object>}
 */
export const updateEducation = async (cvId, educationId, education) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_EDUCATION(cvId, educationId), education);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Delete education entry
 * @param {number} cvId
 * @param {number} educationId
 * @returns {Promise<Object>}
 */
export const deleteEducation = async (cvId, educationId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_EDUCATION(cvId, educationId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Experience ====================

/**
 * Add work experience to CV
 * @param {number} cvId
 * @param {Object} experience
 * @returns {Promise<Object>}
 */
export const addExperience = async (cvId, experience) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_EXPERIENCE(cvId), experience);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update work experience entry
 * @param {number} cvId
 * @param {number} experienceId
 * @param {Object} experience
 * @returns {Promise<Object>}
 */
export const updateExperience = async (cvId, experienceId, experience) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_EXPERIENCE(cvId, experienceId), experience);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Delete work experience entry
 * @param {number} cvId
 * @param {number} experienceId
 * @returns {Promise<Object>}
 */
export const deleteExperience = async (cvId, experienceId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_EXPERIENCE(cvId, experienceId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Skills ====================

/**
 * Add skill to CV
 * @param {number} cvId
 * @param {Object} skill
 * @returns {Promise<Object>}
 */
export const addSkill = async (cvId, skill) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_SKILL(cvId), skill);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update skill entry
 * @param {number} cvId
 * @param {number} skillId
 * @param {Object} skill
 * @returns {Promise<Object>}
 */
export const updateSkill = async (cvId, skillId, skill) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_SKILL(cvId, skillId), skill);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Delete skill entry
 * @param {number} cvId
 * @param {number} skillId
 * @returns {Promise<Object>}
 */
export const deleteSkill = async (cvId, skillId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_SKILL(cvId, skillId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Languages ====================

export const addLanguage = async (cvId, language) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_LANGUAGE(cvId), language);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateLanguage = async (cvId, languageId, language) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_LANGUAGE(cvId, languageId), language);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteLanguage = async (cvId, languageId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_LANGUAGE(cvId, languageId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Certificates ====================

export const addCertificate = async (cvId, certificate) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_CERTIFICATE(cvId), certificate);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateCertificate = async (cvId, certificateId, certificate) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_CERTIFICATE(cvId, certificateId), certificate);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteCertificate = async (cvId, certificateId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_CERTIFICATE(cvId, certificateId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Projects ====================

export const addProject = async (cvId, project) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_PROJECT(cvId), project);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateProject = async (cvId, projectId, project) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_PROJECT(cvId, projectId), project);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteProject = async (cvId, projectId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_PROJECT(cvId, projectId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Publications ====================

export const addPublication = async (cvId, publication) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_PUBLICATION(cvId), publication);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updatePublication = async (cvId, publicationId, publication) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_PUBLICATION(cvId, publicationId), publication);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deletePublication = async (cvId, publicationId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_PUBLICATION(cvId, publicationId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Awards ====================

export const addAward = async (cvId, award) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_AWARD(cvId), award);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateAward = async (cvId, awardId, award) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_AWARD(cvId, awardId), award);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteAward = async (cvId, awardId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_AWARD(cvId, awardId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Hobbies ====================

export const addHobby = async (cvId, hobby) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_HOBBY(cvId), hobby);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateHobby = async (cvId, hobbyId, hobby) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_HOBBY(cvId, hobbyId), hobby);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteHobby = async (cvId, hobbyId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_HOBBY(cvId, hobbyId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Volunteers ====================

export const addVolunteer = async (cvId, volunteer) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_VOLUNTEER(cvId), volunteer);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateVolunteer = async (cvId, volunteerId, volunteer) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_VOLUNTEER(cvId, volunteerId), volunteer);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteVolunteer = async (cvId, volunteerId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_VOLUNTEER(cvId, volunteerId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== Memberships ====================

export const addMembership = async (cvId, membership) => {
  try {
    const response = await http.post(ENDPOINTS.CV.ADD_MEMBERSHIP(cvId), membership);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateMembership = async (cvId, membershipId, membership) => {
  try {
    const response = await http.put(ENDPOINTS.CV.UPDATE_MEMBERSHIP(cvId, membershipId), membership);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteMembership = async (cvId, membershipId) => {
  try {
    const response = await http.delete(ENDPOINTS.CV.DELETE_MEMBERSHIP(cvId, membershipId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== CV Snapshot ====================

/**
 * Create CV snapshot for job application
 * @param {Object} snapshotData
 * @param {number} snapshotData.cvId
 * @param {number} snapshotData.assessmentPlayerId
 * @param {number} snapshotData.assessmentId
 * @param {number} snapshotData.companyId
 * @returns {Promise<Object>}
 */
export const createSnapshot = async (snapshotData) => {
  try {
    const response = await http.post(ENDPOINTS.CV_SNAPSHOT.CREATE, snapshotData);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get CV snapshot by ID
 * @param {number} snapshotId
 * @returns {Promise<Object>}
 */
export const getSnapshot = async (snapshotId) => {
  try {
    const response = await http.get(ENDPOINTS.CV_SNAPSHOT.GET(snapshotId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get CV snapshot by application (AssessmentPlayer) ID
 * @param {number} applicationId
 * @returns {Promise<Object>}
 */
export const getSnapshotByApplication = async (applicationId) => {
  try {
    const response = await http.get(ENDPOINTS.CV_SNAPSHOT.GET_BY_APPLICATION(applicationId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  // JWT-based (recommended for authenticated users)
  findOrCreateMyCv,
  // ID-based
  updateCv,
  // Education
  addEducation,
  updateEducation,
  deleteEducation,
  // Experience
  addExperience,
  updateExperience,
  deleteExperience,
  // Skills
  addSkill,
  updateSkill,
  deleteSkill,
  // Languages
  addLanguage,
  updateLanguage,
  deleteLanguage,
  // Certificates
  addCertificate,
  updateCertificate,
  deleteCertificate,
  // Projects
  addProject,
  updateProject,
  deleteProject,
  // Publications
  addPublication,
  updatePublication,
  deletePublication,
  // Awards
  addAward,
  updateAward,
  deleteAward,
  // Hobbies
  addHobby,
  updateHobby,
  deleteHobby,
  // Volunteers
  addVolunteer,
  updateVolunteer,
  deleteVolunteer,
  // Memberships
  addMembership,
  updateMembership,
  deleteMembership,
  // Snapshot
  createSnapshot,
  getSnapshot,
  getSnapshotByApplication
};
